import { useQuery } from "@tanstack/react-query"
import { CalendarClock, CircleAlert, ListTodo, Lock } from "lucide-react"
import { useParams } from "react-router"
import { api } from "@/api/cliente"
import type { Evento } from "@/componentes/tareas/LineaDeTiempo"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * Una tarea vista desde el enlace del bot. **Sin sesión y sin poder tocar nada.**
 *
 * ⚠ **Vive fuera del `AppShell`, y eso no es maquetación.** Sin barra lateral ni
 * encabezado no hay ningún camino desde aquí a otra pantalla: quien llega con el
 * enlace ve esta tarea y se acaba. Si esto colgara del armazón, un menú lleno de
 * enlaces invitaría a pulsarlos y cada uno respondería 401 — una pantalla que parece
 * rota en vez de una que es deliberadamente pequeña.
 *
 * ⚠ **No hay nada que escribir aquí, ni siquiera deshabilitado.** Un campo apagado
 * dice «podrías, si tuvieras permiso» e invita a buscar cómo. Esto es una lectura.
 *
 * ⚠ **El error es uno solo.** Caducado, revocado, la tarea cerrada o un enlace
 * inventado: el mismo mensaje. Distinguirlos le diría a quien prueba enlaces cuáles
 * llegaron a existir.
 */

type TareaPublica = {
  codigo: string
  titulo: string
  descripcion: string | null
  tipo: string
  estado: string
  prioridad: string
  vence_en: string | null
  asignado_a: string | null
  creado_por: string
  creado_en: string
  microtareas: { titulo: string; hecha: boolean }[]
  eventos: (Omit<Evento, "id" | "actor_id"> & { autor: string })[]
  comentarios: { autor: string; cuerpo: string; creado_en: string }[]
}

const TONO: Record<string, Tono> = {
  URGENTE: "peligro",
  ALTA: "aviso",
  MEDIA: "neutro",
  BAJA: "neutro",
}

function cuando(iso: string): string {
  return comoFechaHora(iso)
}

export function TareaPublica() {
  const { token = "" } = useParams()

  const consulta = useQuery({
    queryKey: ["publico", token],
    queryFn: () => api.get<TareaPublica>(`/api/v1/publico/tareas/${token}`),
    // Sin reintentos: un enlace que no vale no va a valer al segundo intento, y
    // reintentar sólo alarga la espera antes de decirlo.
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado, haberse revocado, o la tarea ya está cerrada. Pídele uno nuevo a
          quien te lo mandó, o entra al sistema.
        </p>
      </div>
    )
  }

  const t = consulta.data
  const avances = t.eventos.filter((e) => e.tipo === "AVANCE")
  const hechas = t.microtareas.filter((m) => m.hecha).length

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-8">
      <p
        className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
        style={{ borderColor: "var(--border)", color: "var(--fg-muted)" }}
      >
        <Lock size={12} aria-hidden={true} />
        Vista de sólo lectura. El enlace caduca a los siete días.
      </p>

      <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
        {t.codigo} · la asignó {t.creado_por}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{t.titulo}</h1>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <Insignia tono={TONO[t.prioridad] ?? "neutro"}>{t.prioridad}</Insignia>
        <Insignia tono="info">{t.estado.replaceAll("_", " ")}</Insignia>
        {t.asignado_a !== null && (
          <span style={{ color: "var(--fg-muted)" }}>Responsable: {t.asignado_a}</span>
        )}
        {t.vence_en !== null && (
          <span
            className="flex items-center gap-1 tabular-nums"
            style={{ color: "var(--fg-muted)" }}
          >
            <CalendarClock size={14} aria-hidden={true} />
            Vence el {comoFecha(t.vence_en)}
          </span>
        )}
      </div>

      {t.descripcion !== null && (
        <p className="mt-4 whitespace-pre-wrap text-sm">{t.descripcion}</p>
      )}

      {t.microtareas.length > 0 && (
        <section className="tarjeta mt-6">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <ListTodo size={14} aria-hidden={true} />
            Microtareas
            <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {hechas}/{t.microtareas.length}
            </span>
          </h2>
          <ul className="mt-2 space-y-1 text-sm">
            {t.microtareas.map((m) => (
              <li
                key={m.titulo}
                style={{
                  textDecoration: m.hecha ? "line-through" : undefined,
                  color: m.hecha ? "var(--fg-muted)" : undefined,
                }}
              >
                {m.hecha ? "✓" : "·"} {m.titulo}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="tarjeta mt-6">
        <h2 className="text-sm font-medium">Avances</h2>
        {avances.length === 0 ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Todavía no hay avances.
          </p>
        ) : (
          <ol className="mt-2 space-y-3">
            {avances.map((e) => (
              <li
                key={`${e.creado_en}-${e.nota}`}
                className="border-l-2 pl-3"
                style={{ borderColor: "var(--border)" }}
              >
                <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {e.autor} · <span className="tabular-nums">{cuando(e.creado_en)}</span>
                  {e.porcentaje !== null && ` · ${e.porcentaje} %`}
                </p>
                <p className="text-sm">{e.nota}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="tarjeta mt-6">
        <h2 className="text-sm font-medium">Conversación</h2>
        {t.comentarios.length === 0 ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Todavía no hay comentarios.
          </p>
        ) : (
          <ul className="mt-2 space-y-3">
            {t.comentarios.map((c) => (
              <li
                key={`${c.creado_en}-${c.autor}`}
                className="border-l-2 pl-3"
                style={{ borderColor: "var(--border)" }}
              >
                <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {c.autor} · <span className="tabular-nums">{cuando(c.creado_en)}</span>
                </p>
                <p className="whitespace-pre-wrap text-sm">{c.cuerpo}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
